import type { Struct } from 'superstruct';
import { omit, record, string } from 'superstruct';
import type {
	AnyStruct,
	ObjectSchema,
	ObjectType,
} from 'superstruct/dist/utils';

import { ObjectEntries, objectFromEntries } from '@adflow/shared/util-js';

export type IdMap<T> = Record<PrefixedId, Omit<T, 'id'>>;

export function IdMapStruct<S extends ObjectSchema & { id: AnyStruct }>(
	struct: Struct<ObjectType<S>, S>
) {
	return record(string(), omit(struct, ['id']));
}

/**
 * The keys in id maps must not look like numbers. Lodash's `set` will create
 * arrays when there is a number or number-like string in the path.
 */
export type PrefixedId = `id_${number}`;

function prefixId(id: number): PrefixedId {
	return `id_${id}`;
}

function unPrefixId(id: PrefixedId): number {
	return Number(id.slice(3));
}

export function arrayToIdMap<T extends { id: number }>(arr: T[]): IdMap<T> {
	return objectFromEntries(arr.map(({ id, ...rest }) => [prefixId(id), rest]));
}

export function idMapToArray<T>(map: IdMap<T>) {
	return ObjectEntries(map).map(([id, rest]) => ({
		...rest,
		id: unPrefixId(id),
	}));
}
